import { useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { apiErrorText, useCreateRequestMutation } from '@shared/api';
import { MTO_SUPPLY } from '@entities/requests';
import { Button } from '@shared/ui/Button';
import { Input, TextArea } from '@shared/ui/Input';
import { Modal } from '@shared/ui/Modal';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const DateInput = styled(Input)`
  max-width: 220px;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};

  b {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Done = styled.div`
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.status.success}1A;
  border: 1px solid ${({ theme }) => theme.colors.status.success}33;
  font-size: 14px;
  line-height: 1.5;
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
`;

interface Props {
  projectId: string;
  revisionId: string;
  /** Шифр документа РД — в название заявки; `null`, пока не загрузился. */
  docCode: string | null;
  onClose: () => void;
}

/**
 * «Передать в снабжение» из объёмов ревизии РД: заявка в снабжение
 * (`mto_supply`) от ПТО по этой ревизии — сразу поданная, она тут же во
 * входящих снабжения. Снабженец по ней сам сформирует ведомость материалов.
 */
export function SendToSupplyDialog({ projectId, revisionId, docCode, onClose }: Props) {
  const [createRequest, { isLoading }] = useCreateRequestMutation();
  const [needBy, setNeedBy] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const title = docCode ? `Материалы по ${docCode}` : 'Материалы по документу РД';

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const res = await createRequest({
        request_type: MTO_SUPPLY,
        source_function: 'pto',
        title,
        project_id: projectId,
        ...(needBy ? { due_date: needBy } : {}),
        payload: {
          revision_ids: [revisionId],
          ...(needBy ? { need_by: needBy } : {}),
          ...(comment.trim() ? { comment: comment.trim() } : {}),
        },
        submit: true,
      }).unwrap();
      setCreatedId(res.request.id);
    } catch (err) {
      setError(apiErrorText(err, 'Заявка не отправлена.'));
    }
  }

  if (createdId) {
    return (
      <Modal open title="Заявка отправлена в снабжение" onClose={onClose}>
        <Column>
          <Done role="status">
            «{title}» — уже во входящих снабжения. Когда снабжение ответит, придёт уведомление, а
            ответ появится в карточке заявки.
          </Done>
          <Buttons>
            <Link to={`/requests/${createdId}`}>Открыть заявку</Link>
            <Button type="button" $variant="secondary" onClick={onClose}>
              Закрыть
            </Button>
          </Buttons>
        </Column>
      </Modal>
    );
  }

  return (
    <Modal open title="Передать в снабжение" onClose={isLoading ? () => undefined : onClose}>
      <Form onSubmit={(e) => void submit(e)}>
        <Hint>
          Снабжение получит заявку <b>«{title}»</b> с этим документом РД и само сформирует по нему
          ведомость материалов.
        </Hint>
        <FieldLabel>
          Нужно к (необязательно)
          <DateInput
            type="date"
            value={needBy}
            onChange={(e) => setNeedBy(e.target.value)}
            autoFocus
          />
        </FieldLabel>
        <FieldLabel>
          Комментарий (необязательно)
          <TextArea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Что важно: какие конструкции в первую очередь, куда везти, кому звонить"
            maxLength={4000}
          />
        </FieldLabel>
        {error && <ErrorText role="alert">{error}</ErrorText>}
        <Buttons>
          <Button type="submit" $variant="primary" disabled={isLoading}>
            {isLoading ? 'Отправка…' : 'Отправить в снабжение'}
          </Button>
          <Button type="button" $variant="secondary" onClick={onClose} disabled={isLoading}>
            Отмена
          </Button>
        </Buttons>
      </Form>
    </Modal>
  );
}
